import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { iamApi, type GrantPolicyRequest, type RevokePolicyGrantRequest } from '@/api/iam.api';

const QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
} as const;

export function usePolicyGrantRecords(policyId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['iam', 'policies', 'grants', policyId] as const,
    queryFn: async () => {
      const response = await iamApi.searchPolicyGrants({
        limit: 100,
        offset: '0',
        policy_id: policyId as string,
      });
      if (response.code !== 0) {
        throw new Error(response.message || '查询策略授权记录失败');
      }
      return response;
    },
    enabled: enabled && !!policyId,
    ...QUERY_OPTIONS,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useUserPolicyGrants(identity: string | undefined) {
  return useQuery({
    queryKey: ['iam', 'grants', 'user', identity] as const,
    queryFn: async () => {
      const response = await iamApi.getUserPolicyGrants(identity as string);
      if (response.code !== 0) {
        throw new Error(response.message || '查询用户权限失败');
      }
      return response;
    },
    enabled: !!identity,
    ...QUERY_OPTIONS,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useSavePolicyGrant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GrantPolicyRequest) => {
      const response = await iamApi.grantPolicy(data);
      if (response.code !== 0) {
        throw new Error(response.message || '保存策略授权失败');
      }
      return response;
    },
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['iam', 'policies'] }),
        queryClient.invalidateQueries({ queryKey: ['iam', 'grants'] }),
      ]);
    },
  });
}

export function useRevokePolicyGrant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RevokePolicyGrantRequest) => {
      const response = await iamApi.revokePolicyGrant(data);
      if (response.code !== 0) {
        throw new Error(response.message || '撤销策略授权失败');
      }
      return response;
    },
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['iam', 'policies'] }),
        queryClient.invalidateQueries({ queryKey: ['iam', 'grants'] }),
      ]);
    },
  });
}
